import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { usePageTitle } from '@/hooks/usePageTitle';
import { allMovies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [movie, setMovie] = useState<Movie | null>(null);
  const [relatedMovies, setRelatedMovies] = useState<Movie[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  useEffect(() => {
    if (id) {
      const found = allMovies.find(m => m.id === Number(id));
      setMovie(found || null);
      if (found) {
        setRelatedMovies(
          allMovies.filter(m => m.categorySlug === found.categorySlug && m.id !== found.id).slice(0, 4)
        );
      }
    }
  }, [id]);

  usePageTitle(movie ? `${movie.title} - 糖心影视 | 高清在线观看` : '影片详情 - 糖心影视');

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-500">影片未找到</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <nav className="px-4 md:px-6 py-3 bg-background-50 border-b border-background-200" aria-label="Breadcrumb">
          <ol className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-foreground-500">
            <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
            <li className="text-foreground-300">/</li>
            <li><Link to={`/category/${movie.categorySlug}`} className="hover:text-primary-500 transition-colors cursor-pointer">{movie.category}</Link></li>
            <li className="text-foreground-300">/</li>
            <li className="text-foreground-700 font-medium truncate max-w-[200px]">{movie.title}</li>
          </ol>
        </nav>

        <article className="py-8 md:py-12 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[420px] flex-shrink-0">
                <div className="rounded-xl overflow-hidden bg-background-100">
                  <img
                    src={movie.image}
                    alt={movie.title}
                    title={`${movie.title} - 糖心影视高清在线观看`}
                    className="w-full aspect-video object-cover"
                  />
                </div>
                <div className="mt-4 p-4 rounded-xl bg-background-100 border border-background-200">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-foreground-400">导演</span>
                      <p className="text-foreground-800 font-medium mt-0.5">{movie.director}</p>
                    </div>
                    <div>
                      <span className="text-foreground-400">年份</span>
                      <p className="text-foreground-800 font-medium mt-0.5">{movie.year}年</p>
                    </div>
                    <div>
                      <span className="text-foreground-400">地区</span>
                      <p className="text-foreground-800 font-medium mt-0.5">{movie.region}</p>
                    </div>
                    <div>
                      <span className="text-foreground-400">时长</span>
                      <p className="text-foreground-800 font-medium mt-0.5">{movie.duration}</p>
                    </div>
                    <div>
                      <span className="text-foreground-400">评分</span>
                      <p className="text-primary-500 font-heading text-lg font-bold mt-0.5">{movie.rating}</p>
                    </div>
                    <div>
                      <span className="text-foreground-400">播放量</span>
                      <p className="text-foreground-800 font-medium mt-0.5">{(movie.views / 10000).toFixed(0)}万</p>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-col gap-2">
                  <button className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm flex items-center justify-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-play-fill"></i>
                    </div>
                    立即播放
                  </button>
                  <button className="w-full py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-lg border border-background-200 transition-colors cursor-pointer whitespace-nowrap text-sm flex items-center justify-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-heart-line"></i>
                    </div>
                    收藏影片
                  </button>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {movie.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs font-medium">
                      {tag}
                    </span>
                  ))}
                  <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-700 text-xs font-medium">
                    {movie.category}
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-4">
                  {movie.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-6">
                  <span>导演：{movie.director}</span>
                  <span className="hidden sm:inline text-foreground-300">|</span>
                  <span>主演：{movie.actors.join('、')}</span>
                  <span className="hidden sm:inline text-foreground-300">|</span>
                  <span>更新于 {movie.updateTime}</span>
                </div>

                <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
                  <p className="text-base">{movie.content}</p>
                </div>

                {relatedMovies.length > 0 && (
                  <div className="mt-10 pt-8 border-t border-background-200">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                      相关推荐
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {relatedMovies.map((rel) => (
                        <Link
                          key={rel.id}
                          to={`/article/${rel.id}`}
                          className="flex items-center gap-3 p-3 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200 hover:border-primary-200 transition-all cursor-pointer group"
                        >
                          <div className="w-24 h-14 rounded-lg overflow-hidden flex-shrink-0">
                            <img src={rel.image} alt={rel.title} className="w-full h-full object-cover" loading="lazy" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                              {rel.title}
                            </h4>
                            <p className="text-xs text-foreground-400 mt-1 line-clamp-1">{rel.description}</p>
                            <span className="text-xs text-primary-500 mt-1 inline-block">{rel.rating}分</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": movie.title,
              "description": movie.description,
              "datePublished": movie.updateTime,
              "author": {
                "@type": "Organization",
                "name": "糖心影视"
              },
              "publisher": {
                "@type": "Organization",
                "name": "糖心影视",
                "url": "https://www.tangxinys.com"
              }
            })
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.tangxinys.com" },
                { "@type": "ListItem", "position": 2, "name": movie.category, "item": `https://www.tangxinys.com/category/${movie.categorySlug}` },
                { "@type": "ListItem", "position": 3, "name": movie.title }
              ]
            })
          }}
        />
      </main>
      <Footer />
    </>
  );
}